"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "next-themes";
import { useEffect, useState } from "react";

import { createQueryClient } from "@/lib/query-client";
import { initErrorReporting, reportError } from "@/lib/report-error";

import { AuthProvider } from "@/components/auth/auth-provider";
import { MotionProvider } from "@/components/motion/motion-provider";
import { MswProvider } from "@/test/msw/MswProvider";

/**
 * App-wide client providers: dev API mocking (MSW) + theme (dark-only) +
 * TanStack Query (server state). The QueryClient is created in state so it is
 * stable per browser session and never shared across requests on the server.
 * Also boots error reporting and captures uncaught window errors (ENGINEERING §6).
 */
export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => createQueryClient());

  useEffect(() => {
    initErrorReporting();

    const onError = (event: ErrorEvent) =>
      reportError(event.error ?? event.message, { source: "window.onerror" });
    const onRejection = (event: PromiseRejectionEvent) =>
      reportError(event.reason, { source: "window.unhandledrejection" });

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return (
    <MswProvider>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        forcedTheme="dark"
        enableSystem={false}
        disableTransitionOnChange
      >
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <MotionProvider>{children}</MotionProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </AuthProvider>
      </ThemeProvider>
    </MswProvider>
  );
}
